import { css, cx } from '@emotion/css';
import { isEmpty } from 'lodash/fp';

import { FieldLabel, FormGroup } from '../../components';
import { styled } from '../../theme';
import ToggleStrip from './ToggleStrip';
import { HorizontalButtonGroupProps, ToggleOption } from './types';

const fullWidthStripClass = css({ width: '100%' });

const StyledFormGroup = styled(FormGroup)<{ $fullWidth?: boolean }>(({ $fullWidth }) => ({
    ...($fullWidth && { width: '100%' }),
}));

const GroupRow = styled('div')<{ $fullWidth?: boolean }>(({ $fullWidth }) => ({
    display: 'flex',
    flexDirection: 'row',
    width: $fullWidth ? '100%' : 'fit-content',
    alignItems: 'stretch',
}));

const HorizontalButtonGroup = <T,>({
    className,
    stripClassName,
    fullWidth,
    label,
    labelStyles,
    value,
    options,
    mapValueToLabel,
    onSelect,
    buttonStyle,
    size,
    renderTooltipTitle,
    optionDisabledGetter,
    height,
    ariaLabel,
    tooltipOffset,
}: HorizontalButtonGroupProps<T>) => {
    if (isEmpty(options) || options.length === 1) {
        return null;
    }

    const optionValuesByKey = options.reduce<Record<string, T>>((acc, optionValue) => {
        acc[String(optionValue)] = optionValue;
        return acc;
    }, {});

    const toggleOptions: ToggleOption[] = options.map((optionValue) => ({
        value: String(optionValue),
        label: mapValueToLabel ? mapValueToLabel(optionValue) : String(optionValue),
        tooltipTitle: renderTooltipTitle?.(optionValue),
        isDisabled: optionDisabledGetter?.(optionValue),
    }));

    const handleChange = (selectedValue: string) => {
        onSelect(optionValuesByKey[selectedValue]);
    };

    const resolvedHeight =
        height ??
        (size === 'xs'
            ? 28
            : Number(buttonStyle?.height) || Number(buttonStyle?.minHeight) || undefined);

    const strip = (
        <ToggleStrip
            value={value == null ? '' : String(value)}
            options={toggleOptions}
            onChange={handleChange}
            height={resolvedHeight}
            buttonStyle={buttonStyle}
            size={size}
            ariaLabel={ariaLabel ?? label}
            className={cx(fullWidth && fullWidthStripClass, stripClassName)}
            tooltipOffset={tooltipOffset}
        />
    );

    return (
        <StyledFormGroup className={className} $fullWidth={fullWidth}>
            {label && <FieldLabel style={labelStyles}>{label}</FieldLabel>}
            <GroupRow $fullWidth={fullWidth}>{strip}</GroupRow>
        </StyledFormGroup>
    );
};

export default HorizontalButtonGroup;
